import { FC, useEffect, useState } from "react"
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material"
import { Userbot } from "../../interfaces"
import { useChainProgressQuery } from "../../store"
import { getApiErrorMessage } from "@/core/utils"

/** Форматирует миллисекунды в человекочитаемый countdown (TZ §5: клиентский, без запросов). */
export const formatCountdown = (ms: number): string => {
  if (ms <= 0) return "0 c"
  const totalSec = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSec / 60)
  const seconds = totalSec % 60
  return `${minutes} мин ${seconds} с`
}

interface IProps {
  chainId: string
}

/** Live-прогресс цепочки: polling :id/progress, park countdown, branchChoices (SPEC §10). */
export const ChainProgressPanel: FC<IProps> = ({ chainId }) => {
  const { data, isFetching, isError, error } = useChainProgressQuery(
    { id: chainId },
    {
      pollingInterval: 60000,
    },
  )

  // Клиентский countdown до resumeAt (обновляется раз в секунду, без запросов к бэку)
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  if (isFetching && !data) return <CircularProgress size={20} />
  if (isError)
    return <Alert severity="error">{getApiErrorMessage(error)}</Alert>
  if (!data) return null

  const parkCountdownMs = data.park ? data.park.resumeAt - now : 0

  return (
    <Stack spacing={1}>
      <Box display="flex" gap={1} flexWrap="wrap">
        <Chip
          color="success"
          size="small"
          label={`completed: ${data.completed}`}
        />
        <Chip color="error" size="small" label={`failed: ${data.failed}`} />
        <Chip color="warning" size="small" label={`skipped: ${data.skipped}`} />
      </Box>

      {data.park && (
        <Box>
          <Typography variant="body2">
            Стоит на{" "}
            <Typography component="span" fontFamily="monospace">
              {data.park.nodeKey}
            </Typography>
            , ждёт до {new Date(data.park.resumeAt).toLocaleString("ru")}
            {parkCountdownMs > 0 && (
              <Typography component="span" color="text.secondary">
                {" "}
                (через {formatCountdown(parkCountdownMs)})
              </Typography>
            )}
          </Typography>
        </Box>
      )}

      {data.branchChoices.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Выбор веток
          </Typography>
          <Stack spacing={0.5}>
            {data.branchChoices.map((choice, i) => (
              <Typography key={i} variant="body2">
                <Typography component="span" fontFamily="monospace">
                  {choice.nodeKey}
                </Typography>{" "}
                → case {choice.case ?? "не сопоставлено"}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  )
}
